import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Popconfirm, message, Card, Statistic, Image, Spin, Empty, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CameraOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getSellingsApi, deleteSellingApi } from '../../api/sales';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatDate, getPaymentStatusColor } from '../../utils/formatters';
import { WEIGHT_UNITS, SELLING_PHOTO } from '../../utils/constants';
import SaleDetailModal from '../../components/SaleForm/SaleDetailModal';

export default function SalesTab({ cropId, externalTotal, onRefresh }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { farmId } = useParams();
  const [sales, setSales] = useState([]);
  const [totalSelling, setTotalSelling] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detailSale, setDetailSale] = useState(null);

  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSellingsApi({ crop_id: cropId });
      setSales(res.data?.data || []);
      setTotalSelling(res.data?.totalSelling || 0);
    } catch {
      message.error('Failed to load sales');
    } finally {
      setLoading(false);
    }
  }, [cropId]);

  useEffect(() => { fetchSales(); }, [fetchSales]);

  const refresh = () => { fetchSales(); onRefresh?.(); };

  const handleDelete = async (id, amount) => {
    try {
      await deleteSellingApi({ id, amount, cropId });
      message.success('Sale deleted');
      refresh();
    } catch {
      message.error('Failed to delete sale');
    }
  };

  const columns = [
    { title: t('quantity'), key: 'weight', render: (_, r) => `${r.weight} ${WEIGHT_UNITS[r.weightUnit] || r.weightUnit}` },
    { title: t('rate'), key: 'rate', render: (_, r) => `${formatCurrency(r.rate)} / ${r.rateUnit}` },
    {
      title: t('total'), key: 'amount',
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontWeight: 600, color: '#52c41a' }}>{formatCurrency(r.amount)}</span>
          <Tag
            color={getPaymentStatusColor(r.payment_status)}
            style={{ margin: 0, fontSize: 11 }}
          >
            {r.payment_status === 'credit' ? 'Received' : r.payment_status === 'partial' ? 'Partial' : 'Outstanding'}
          </Tag>
        </div>
      ),
    },
    { title: t('date'), dataIndex: 'sell_date', render: formatDate },
    { title: t('buyer'), dataIndex: 'buyer_name', render: (v, r) => <span style={{ textTransform: 'capitalize' }}>{v || r.vepari || '—'}</span> },
    {
      title: t('photo'), key: 'photo',
      render: (_, r) => {
        const url = r.photo_url || (r.photo ? SELLING_PHOTO(r.photo) : null);
        return url ? <Image src={url} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} /> : <CameraOutlined style={{ color: '#ccc' }} />;
      },
    },
    {
      title: 'Actions', key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => setDetailSale(record)} />
          <Button size="small" icon={<EditOutlined />} onClick={() => navigate(`/farms/${farmId}/farm/${cropId}/add-sale`, { state: { editData: record } })} />
          <Popconfirm title="Delete this sale?" onConfirm={() => handleDelete(record.id, record.amount)} okButtonProps={{ danger: true }}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Card style={{ borderRadius: 8, background: '#f6ffed', border: 'none' }} bodyStyle={{ padding: '8px 16px' }}>
          <Statistic title={t('total_selling')} value={formatCurrency(externalTotal || totalSelling)} valueStyle={{ color: '#52c41a', fontSize: 20 }} />
        </Card>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`/farms/${farmId}/farm/${cropId}/add-sale`)}
          style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>{t('add_sale')}</Button>
      </div>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
      ) : sales.length === 0 ? (
        <Empty description="No sales yet">
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`/farms/${farmId}/farm/${cropId}/add-sale`)}
            style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>{t('add_sale')}</Button>
        </Empty>
      ) : (
        <Table dataSource={sales} columns={columns} rowKey="id" scroll={{ x: 600 }} />
      )}
      <SaleDetailModal
        open={!!detailSale}
        onClose={() => setDetailSale(null)}
        sale={detailSale}
      />
    </div>
  );
}
